/**
 * Excavation mode: build drill sites, extract artifact, carry to win.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, PlayerState, DrillSiteState, CarryableObjectState, GameState, Vec3, ModeConfig } from '../types';
import { DEFAULT_DRILL_BUILD_TIME, DEFAULT_DRILL_OPERATION_TIME, DEFAULT_DRILL_SITE_HP } from '../constants';
import { arcDistance } from './gameState';
import { dcos, dsin } from './dmath';

/**
 * Choose an artifact planet and surface location.
 */
export function initExcavation(
  planets: PlanetState[],
  rng: { int: (min: number, max: number) => number; range: (min: number, max: number) => number }
): { artifactPlanetIndex: number; artifactSurfaceDir: Vec3 } {
  const spawnablePlanets = planets
    .map((p, i) => ({ planet: p, index: i }))
    .filter(e => e.planet.type === 'planet');

  if (spawnablePlanets.length === 0) {
    return { artifactPlanetIndex: 0, artifactSurfaceDir: { x: 0, y: 1, z: 0 } };
  }

  const chosen = spawnablePlanets[rng.int(0, spawnablePlanets.length - 1)];

  const theta = rng.range(0, Math.PI * 2);
  const phi = rng.range(0.3, Math.PI - 0.3);
  const surfaceDir: Vec3 = {
    x: dsin(phi) * dcos(theta),
    y: dcos(phi),
    z: dsin(phi) * dsin(theta),
  };

  return {
    artifactPlanetIndex: chosen.index,
    artifactSurfaceDir: surfaceDir,
  };
}

/**
 * Check if a player can build a drill site near the artifact location.
 */
export function canBuildDrill(
  player: PlayerState,
  planets: PlanetState[],
  artifactPlanetIndex: number,
  artifactSurfaceDir: Vec3
): boolean {
  if (!player.alive || player.airborne) return false;
  if (player.planetIndex !== artifactPlanetIndex) return false;
  if (player.movementRemaining < 1.0) return false;

  const planet = planets[artifactPlanetIndex];
  if (!planet) return false;

  const dist = arcDistance(player.surfaceDir, artifactSurfaceDir, planet.radius);
  return dist <= 5.0; // generous range for drill placement
}

/**
 * Build a new drill site.
 */
export function buildDrillSite(
  state: GameState,
  playerId: number,
  surfaceDir: Vec3
): GameState {
  const ex = state.modeState.excavation;
  if (!ex) return state;

  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;

  const nextId = ex.drillSites.length > 0
    ? Math.max(...ex.drillSites.map(d => d.id)) + 1
    : 0;

  const config = state.config.modeConfig;
  const buildTime = config.drillBuildTime ?? DEFAULT_DRILL_BUILD_TIME;
  const operationTime = config.drillOperationTime ?? DEFAULT_DRILL_OPERATION_TIME;
  const hp = config.drillSiteHP ?? DEFAULT_DRILL_SITE_HP;

  const newDrill: DrillSiteState = {
    id: nextId,
    teamIndex: player.teamIndex,
    planetIndex: ex.artifactPlanetIndex,
    surfaceDir: { ...surfaceDir },
    buildTurnsRemaining: buildTime,
    operationTurnsRemaining: operationTime,
    built: false,
    operating: false,
    extracted: false,
    health: hp,
  };

  const updatedPlayers = state.players.map(p =>
    p.id === playerId ? { ...p, movementRemaining: p.movementRemaining - 1.0 } : p
  );

  return {
    ...state,
    players: updatedPlayers,
    modeState: {
      ...state.modeState,
      excavation: { ...ex, drillSites: [...ex.drillSites, newDrill] },
    },
  };
}

/**
 * Tick a single drill site: progress build or operation.
 */
export function tickDrillSite(drillSite: DrillSiteState): DrillSiteState {
  if (drillSite.health <= 0) return drillSite;
  if (drillSite.extracted) return drillSite;

  if (!drillSite.built) {
    const remaining = drillSite.buildTurnsRemaining - 1;
    if (remaining <= 0) {
      return { ...drillSite, buildTurnsRemaining: 0, built: true, operating: true };
    }
    return { ...drillSite, buildTurnsRemaining: remaining };
  }

  if (drillSite.operating) {
    const remaining = drillSite.operationTurnsRemaining - 1;
    if (remaining <= 0) {
      return { ...drillSite, operationTurnsRemaining: 0, operating: false, extracted: true };
    }
    return { ...drillSite, operationTurnsRemaining: remaining };
  }

  return drillSite;
}

/**
 * Tick all drill sites in the game state.
 */
export function tickAllDrillSites(state: GameState): GameState {
  const ex = state.modeState.excavation;
  if (!ex) return state;

  const updatedDrills = ex.drillSites.map(d => tickDrillSite(d));

  return {
    ...state,
    modeState: {
      ...state.modeState,
      excavation: { ...ex, drillSites: updatedDrills },
    },
  };
}

/**
 * Check if any drill has completed extraction.
 */
export function checkDrillExtraction(state: GameState): { extracted: boolean; teamIndex: number | null } {
  const ex = state.modeState.excavation;
  if (!ex) return { extracted: false, teamIndex: null };

  for (const drill of ex.drillSites) {
    if (drill.extracted && drill.health > 0) {
      return { extracted: true, teamIndex: drill.teamIndex };
    }
  }

  return { extracted: false, teamIndex: null };
}

/**
 * Create a carryable artifact after extraction completes.
 */
export function createArtifactCarryable(state: GameState, teamIndex: number): GameState {
  const ex = state.modeState.excavation;
  if (!ex) return state;

  const nextId = state.carryables.length > 0
    ? Math.max(...state.carryables.map(c => c.id)) + 1
    : 0;

  const artifactCarryable: CarryableObjectState = {
    id: nextId,
    type: 'artifact',
    teamIndex: -1, // neutral — anyone can pick up
    planetIndex: ex.artifactPlanetIndex,
    surfaceDir: { ...ex.artifactSurfaceDir },
    carriedBy: null,
    originPlanetIndex: ex.artifactPlanetIndex,
    originSurfaceDir: { ...ex.artifactSurfaceDir },
    alive: true,
  };

  return {
    ...state,
    carryables: [...state.carryables, artifactCarryable],
    modeState: {
      ...state.modeState,
      excavation: { ...ex, artifactExtracted: true, artifactCarryableId: nextId },
    },
  };
}
